import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Upload, Download, Trash2 } from "lucide-react";

const TIPO_LABEL = {
  csf: "Constancia de situación fiscal (CSF)",
  contrato_firmado: "Contrato firmado",
  otro_administrativo: "Otro documento administrativo",
};

export default function DocumentosTab({ oid }) {
  const [docs, setDocs] = useState([]);
  const [tipo, setTipo] = useState("csf");
  const [subiendo, setSubiendo] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/organizations/${oid}/documentos`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [oid]);

  const subir = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("tipo_documento", tipo);
      fd.append("archivo", file);
      await api.post(`/organizations/${oid}/documentos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      notifyOk("Documento guardado.");
      load();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubiendo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const descargar = async (d) => {
    try {
      const r = await api.get(`/organizations/${oid}/documentos/${d.id}/archivo`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.nombre_archivo || "documento";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notifyError("No se pudo descargar el documento.");
    }
  };

  const eliminar = async (d) => {
    try {
      await api.delete(`/organizations/${oid}/documentos/${d.id}`);
      load();
    } catch (e) {
      notifyError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="tab-documentos">
      <p className="text-xs text-slate-500 max-w-2xl">
        Ningún documento es obligatorio para provisionar accesos. No se sube identificación oficial
        ni se guarda su número: la verificación de identidad se registra como acto humano en
        Representación. Toda descarga queda auditada.
      </p>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-100 p-4">
        <div className="w-full sm:w-64">
          <p className="text-xs font-medium text-slate-500 mb-1">Tipo de documento</p>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="rounded-xl" data-testid="doc-tipo-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TIPO_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <input ref={fileRef} type="file" data-testid="doc-file-input" className="hidden"
          onChange={(e) => subir(e.target.files?.[0])} />
        <Button data-testid="doc-upload-button" disabled={subiendo} onClick={() => fileRef.current?.click()}
          className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white w-full sm:w-auto">
          <Upload className="h-4 w-4 mr-1.5" /> {subiendo ? "Subiendo…" : "Subir documento"}
        </Button>
      </div>

      {docs.length === 0 ? (
        <p data-testid="org-docs-empty" className="text-slate-400 text-sm">Sin documentos cargados (opcional).</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
          {docs.map((d) => (
            <div key={d.id} data-testid={`org-doc-${d.id}`} className="px-5 py-4 flex items-center gap-3">
              <FileText className="h-5 w-5 text-[#012472] shrink-0" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#012472] truncate">{TIPO_LABEL[d.tipo_documento] || d.tipo_documento}</p>
                <p className="text-xs text-slate-500 truncate">{d.nombre_archivo} · {Math.round((d.tamano || 0) / 1024)} KB</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-lg" data-testid={`doc-download-${d.id}`} onClick={() => descargar(d)}>
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="rounded-lg text-slate-400" data-testid={`doc-delete-${d.id}`} onClick={() => eliminar(d)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
